import React, {
  useState, useMemo, useRef, useEffect, useCallback,
} from 'react';
import { Form, InputGroup } from '@openedx/paragon';
import { Search } from '@openedx/paragon/icons';
import { getEnterpriseMemberships } from '../../../data/api';

export interface LearnerOption {
  name: string;
  email: string;
  username: string;
}

interface LearnerSearchDropdownProps {
  learners: LearnerOption[];
  value: string;
  onChange: (value: string) => void;
  onSelect: (learner: LearnerOption) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  companyName?: string;
  rosterIncomplete?: boolean;
}

const LearnerSearchDropdown: React.FC<LearnerSearchDropdownProps> = ({
  learners,
  value,
  onChange,
  onSelect,
  onFocus,
  onBlur,
  placeholder = 'Search by name or email...',
  disabled = false,
  id = 'learner-search-dropdown',
  companyName,
  rosterIncomplete = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const membershipLookupId = useRef(0);
  const [membershipLearner, setMembershipLearner] = useState<LearnerOption | null>(null);
  const [isLookingUpMembership, setIsLookingUpMembership] = useState(false);

  const searchOptions = useMemo(() => {
    if (!membershipLearner || learners.some((learner) => learner.email === membershipLearner.email)) {
      return learners;
    }
    return [...learners, membershipLearner];
  }, [learners, membershipLearner]);

  // Filter learners based on search term
  const filteredLearners = useMemo(() => {
    if (!value.trim()) { return searchOptions; }
    const term = value.toLowerCase();
    return searchOptions.filter(
      (l) => l.name.toLowerCase().includes(term)
        || l.email.toLowerCase().includes(term)
        || l.username.toLowerCase().includes(term),
    );
  }, [searchOptions, value]);

  // Whether the current value exactly matches a selected learner display string
  const isLearnerSelected = useMemo(
    () => searchOptions.some((l) => `${l.name} (${l.email})` === value),
    [searchOptions, value],
  );

  const showNotFound = value.trim().length > 0 && filteredLearners.length === 0 && !isLearnerSelected;
  const showRosterLoadingHint = showNotFound && rosterIncomplete;

  // Close on click-outside
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    membershipLookupId.current += 1;
    onChange(e.target.value);
    setMembershipLearner(null);
    setIsLookingUpMembership(false);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    onFocus?.();
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // If focus is moving to an element inside the dropdown container (e.g. a list item),
    // don't fire onBlur — the user is still interacting with the dropdown.
    if (containerRef.current && containerRef.current.contains(e.relatedTarget as Node)) {
      return;
    }
    onBlur?.();
  };

  const handleSelect = useCallback(
    (learner: LearnerOption) => {
      onSelect(learner);
      onChange(`${learner.name} (${learner.email})`);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onSelect, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && e.key !== 'ArrowDown') { return; }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => (prev + 1) % Math.max(filteredLearners.length, 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + Math.max(filteredLearners.length, 1)) % Math.max(filteredLearners.length, 1),
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredLearners[highlightedIndex]) {
          handleSelect(filteredLearners[highlightedIndex]);
        } else if (filteredLearners.length === 1) {
          handleSelect(filteredLearners[0]);
        } else {
          const email = value.trim().toLowerCase();
          if (companyName && /^\S+@\S+\.\S+$/.test(email)) {
            const lookupId = membershipLookupId.current + 1;
            membershipLookupId.current = lookupId;
            setIsLookingUpMembership(true);
            getEnterpriseMemberships(email)
              .then((memberships) => {
                if (lookupId !== membershipLookupId.current) {
                  return;
                }
                const membership = memberships.find((entry) => entry.company_name === companyName);
                if (membership) {
                  setMembershipLearner({
                    name: membership.name || email,
                    email,
                    username: membership.username || email,
                  });
                }
              })
              .catch(() => {
                if (lookupId === membershipLookupId.current) {
                  setMembershipLearner(null);
                }
              })
              .finally(() => {
                if (lookupId === membershipLookupId.current) {
                  setIsLookingUpMembership(false);
                }
              });
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  const handleClear = () => {
    membershipLookupId.current += 1;
    onChange('');
    setMembershipLearner(null);
    setIsLookingUpMembership(false);
    setIsOpen(true);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className="learner-search-dropdown" ref={containerRef} style={{ position: 'relative' }}>
      <InputGroup>
        <InputGroup.Prepend>
          <InputGroup.Text>
            <Search size="sm" />
          </InputGroup.Text>
        </InputGroup.Prepend>
        <Form.Control
          ref={inputRef}
          id={id}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {value && (
          <InputGroup.Append>
            <InputGroup.Text
              onClick={handleClear}
              style={{ cursor: 'pointer' }}
              title="Clear"
            >
              ×
            </InputGroup.Text>
          </InputGroup.Append>
        )}
      </InputGroup>

      {isOpen && !isLearnerSelected && (
        <div
          className="dropdown-menu show w-100 mt-1"
          style={{ maxHeight: '260px', overflowY: 'auto', zIndex: 1050 }}
        >
          {filteredLearners.length > 0
            ? filteredLearners.map((learner, index) => (
              <button
                key={learner.username}
                type="button"
                className={`dropdown-item${index === highlightedIndex ? ' active' : ''}`}
                onClick={() => handleSelect(learner)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <span className="font-weight-bold">{learner.name}</span>
                <span className="text-muted ml-1">({learner.email})</span>
              </button>
            ))
            : isLookingUpMembership && (
              <div className="dropdown-item disabled text-muted">Looking up learner...</div>
            )}
          {showRosterLoadingHint && (
            <div className="dropdown-item disabled text-muted">
              Learner list is still loading. Try searching by the learner&apos;s email address and press Enter.
            </div>
          )}
          {showNotFound && !showRosterLoadingHint && !isLookingUpMembership && (
            <div className="dropdown-item disabled text-muted">
              No learner found matching &ldquo;{value}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LearnerSearchDropdown;
